/**
 * samlla#0xhq (a): what the API client does with a 401.
 *
 * A 401 while the Kinde session is still valid (e.g. the backend rejects the
 * token) would bounce /auth/login → /app/journal forever. The guard allows at
 * most one login redirect per window; a second 401 inside it — including the
 * return from login — shows a neutral "session not confirmed" state instead.
 * It never logs the user out and ignores auth routes themselves.
 */
export const AUTH_REDIRECT_KEY = 'samlla:auth-redirect-at';
export const AUTH_REDIRECT_WINDOW_MS = 30_000;

export type UnauthorizedAction = 'ignore' | 'redirect' | 'session-unconfirmed';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

// In-memory fallback so a missing or throwing sessionStorage still bounds
// sequential redirects to one per window (per page lifetime).
let memoryLast: number | null = null;

/** Test hook: forget the in-memory redirect timestamp. */
export function resetAuthRedirectMemory(): void {
	memoryLast = null;
}

export function decideUnauthorized({
	pathname,
	now,
	storage
}: {
	pathname: string;
	now: number;
	storage: StorageLike | null;
}): UnauthorizedAction {
	if (pathname.startsWith('/auth') || pathname.startsWith('/api/auth')) return 'ignore';
	let stored = Number.NaN;
	try {
		stored = Number(storage?.getItem(AUTH_REDIRECT_KEY) ?? Number.NaN);
	} catch {
		// storage unavailable: the in-memory timestamp still applies
	}
	const recent = [stored, memoryLast ?? Number.NaN].some(
		(last) => Number.isFinite(last) && now - last >= 0 && now - last < AUTH_REDIRECT_WINDOW_MS
	);
	if (recent) return 'session-unconfirmed';
	memoryLast = now;
	try {
		storage?.setItem(AUTH_REDIRECT_KEY, String(now));
	} catch {
		// best effort — memoryLast covers this page
	}
	return 'redirect';
}
